import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type SyntheticEvent } from "react";

import type {
  OpsSupportLaunchResponse,
  OpsSupportSession,
  OpsSupportTargetTool,
} from "../../shared/operations-schemas";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Field, FieldError, FieldLabel } from "../components/ui/field";
import { Textarea } from "../components/ui/textarea";
import {
  listSupportSessions,
  listSupportTargets,
  revokeSupportSession,
  startSupportSession,
} from "./api";
import { SupportAccessRequests } from "./SupportAccessRequests";
import { SupportSessionCard } from "./SupportSessionCard";
import { SupportToolFrame } from "./SupportToolFrame";

/** Stable empties, so a pending query does not hand a child a new array identity every render. */
const EMPTY_USERS: { id: string; displayName: string; username: string }[] = [];
const EMPTY_TOOLS: OpsSupportTargetTool[] = [];
const EMPTY_SESSIONS: OpsSupportSession[] = [];

function ActiveSupportSessions({
  organizationId,
  sessions,
  tools,
  onLaunch,
  onEnd,
  ending,
}: {
  organizationId: string;
  sessions: OpsSupportSession[];
  tools: OpsSupportTargetTool[];
  onLaunch: (launch: OpsSupportLaunchResponse) => void;
  onEnd: (supportSessionId: string) => void;
  ending: boolean;
}) {
  return sessions.map((session) => (
    <SupportSessionCard
      key={session.id}
      organizationId={organizationId}
      session={session}
      tools={tools}
      onLaunch={onLaunch}
      onEnd={onEnd}
      ending={ending}
    />
  ));
}

function SupportModePicker({
  mode,
  canWrite,
  onChange,
}: {
  mode: "read" | "write";
  canWrite: boolean;
  onChange: (mode: "read" | "write") => void;
}) {
  return (
    <div className="flex gap-2">
      <Button
        type="button"
        variant={mode === "read" ? "default" : "outline"}
        onClick={() => {
          onChange("read");
        }}
      >
        Read-only
      </Button>
      {canWrite ? (
        <Button
          type="button"
          variant={mode === "write" ? "destructive" : "outline"}
          onClick={() => {
            onChange("write");
          }}
        >
          Write access
        </Button>
      ) : null}
    </div>
  );
}

function SupportAccessForm({
  organizationId,
  canWrite,
  users,
}: {
  organizationId: string;
  canWrite: boolean;
  users: { id: string; displayName: string; username: string }[];
}) {
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<"read" | "write">("read");
  const start = useMutation({
    mutationFn: (input: {
      reason: string;
      targetUserId: string;
      mode: "read" | "write";
      confirmWrite: boolean;
    }) => startSupportSession(organizationId, input),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["ops", "support", organizationId] });
    },
  });
  function submit(event: SyntheticEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    const targetUserId = form.get("targetUserId");
    const reason = form.get("reason");
    if (typeof targetUserId !== "string" || typeof reason !== "string") return;
    start.mutate({ targetUserId, reason, mode, confirmWrite: mode === "write" });
  }
  return (
    <form className="grid gap-3" onSubmit={submit}>
      <Field>
        <FieldLabel htmlFor="support-reason">Reason</FieldLabel>
        <Textarea id="support-reason" name="reason" minLength={10} maxLength={500} required />
      </Field>
      <Field>
        <FieldLabel htmlFor="support-user">Customer user</FieldLabel>
        <select
          className="h-9 w-full rounded-md border bg-background px-3 text-sm"
          id="support-user"
          name="targetUserId"
          required
        >
          <option value="">Select a customer user</option>
          {users.map((user) => (
            <option key={user.id} value={user.id}>
              {user.displayName} ({user.username})
            </option>
          ))}
        </select>
      </Field>
      <SupportModePicker mode={mode} canWrite={canWrite} onChange={setMode} />
      {start.error ? <FieldError>{start.error.message}</FieldError> : null}
      <Button className="w-fit" type="submit" disabled={start.isPending || users.length === 0}>
        Start one-hour support session
      </Button>
    </form>
  );
}

/**
 * The direct path ADR-0062 accepted, kept alongside customer-approved access (ADR-0072).
 *
 * The rendered copy names neither decision: a person deciding whether to use this form cannot act
 * on a document number, and the console is not where our change history belongs.
 *
 * It sits below the request card rather than above it, and says in its own heading that it asks
 * nobody, so an employee choosing it is choosing it rather than reaching for the first form.
 */
function UnapprovedSupportAccess({
  organizationId,
  canWrite,
  sessions,
  tools,
  users,
  onLaunch,
  onEnd,
  ending,
  endError,
}: {
  organizationId: string;
  canWrite: boolean;
  sessions: OpsSupportSession[];
  tools: OpsSupportTargetTool[];
  users: { id: string; displayName: string; username: string }[];
  onLaunch: (launch: OpsSupportLaunchResponse) => void;
  onEnd: (supportSessionId: string) => void;
  ending: boolean;
  endError: string | null;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Support access without customer approval</CardTitle>
        <CardDescription>
          Starts an expiring, employee-attributed session as one customer user without asking anyone
          at the customer first. Prefer a request above unless the situation genuinely cannot wait;
          every use of this form is audited as an unapproved session.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <ActiveSupportSessions
          organizationId={organizationId}
          sessions={sessions}
          tools={tools}
          onLaunch={onLaunch}
          onEnd={onEnd}
          ending={ending}
        />
        {endError ? <FieldError>{endError}</FieldError> : null}
        <SupportAccessForm organizationId={organizationId} canWrite={canWrite} users={users} />
      </CardContent>
    </Card>
  );
}

export function SupportAccess({
  organizationId,
  canStart,
  canWrite,
}: {
  organizationId: string;
  canStart: boolean;
  canWrite: boolean;
}) {
  const sessions = useQuery({
    queryKey: ["ops", "support", organizationId],
    queryFn: () => listSupportSessions(organizationId),
    enabled: canStart,
  });
  const targets = useQuery({
    queryKey: ["ops", "support-targets", organizationId],
    queryFn: () => listSupportTargets(organizationId),
    enabled: canStart,
  });
  const [activeLaunch, setActiveLaunch] = useState<OpsSupportLaunchResponse | null>(null);
  const queryClient = useQueryClient();
  const revoke = useMutation({
    mutationFn: (supportSessionId: string) =>
      revokeSupportSession(organizationId, supportSessionId),
    onSuccess: async () => {
      setActiveLaunch(null);
      await queryClient.invalidateQueries({ queryKey: ["ops", "support", organizationId] });
    },
  });
  const users = targets.data?.users ?? EMPTY_USERS;
  const tools = targets.data?.tools ?? EMPTY_TOOLS;
  const allSessions = sessions.data?.supportSessions ?? EMPTY_SESSIONS;
  // Split by provenance. A session opened under an approval belongs beside that approval; only the
  // ones nobody approved belong under a heading that says so.
  const approvedSessions = allSessions.filter((session) => session.accessRequestId !== null);
  const unapprovedSessions = allSessions.filter((session) => session.accessRequestId === null);
  const refreshSessions = async () => {
    await queryClient.invalidateQueries({ queryKey: ["ops", "support", organizationId] });
  };
  const endSession = (supportSessionId: string) => {
    revoke.mutate(supportSessionId);
  };
  if (!canStart) return null;
  return (
    <div className="space-y-5">
      <SupportAccessRequests
        organizationId={organizationId}
        canStart={canStart}
        canWrite={canWrite}
        users={users}
        tools={tools}
        sessions={approvedSessions}
        onSessionStarted={refreshSessions}
        onLaunch={setActiveLaunch}
        onEnd={endSession}
        ending={revoke.isPending}
      />
      <UnapprovedSupportAccess
        organizationId={organizationId}
        canWrite={canWrite}
        sessions={unapprovedSessions}
        tools={tools}
        users={users}
        onLaunch={setActiveLaunch}
        onEnd={endSession}
        ending={revoke.isPending}
        endError={revoke.error ? revoke.error.message : null}
      />
      {/* One frame for the page: either path can open a tool, and only one can be open at a time. */}
      {activeLaunch ? (
        <SupportToolFrame
          launch={activeLaunch}
          onClose={() => {
            setActiveLaunch(null);
          }}
        />
      ) : null}
    </div>
  );
}
